import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { useLanguage } from '../LanguageContext';
import translations from '../translations';

function Dashboard({ orgId }) {
  const { language } = useLanguage();
  const t = translations[language];
  const [checkedInKids, setCheckedInKids] = useState([]);
  const [waitingKids, setWaitingKids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedKid, setSelectedKid] = useState(null);

  const fetchKids = async () => {
    if (!orgId) {
      setLoading(false);
      return;
    }

    // Fetch all active checkins (checked_in and parent_waiting)
    const { data: checkins, error: checkinError } = await supabase
      .from('checkins')
      .select('*')
      .eq('organization_id', orgId)
      .in('status', ['checked_in', 'parent_waiting'])
      .order('checked_in_at', { ascending: false });

    if (checkinError) {
      setLoading(false);
      return;
    }

    if (!checkins.length) {
      setCheckedInKids([]);
      setWaitingKids([]);
      setLoading(false);
      return;
    }

    const childIds = checkins.map((c) => c.child_id);
    const { data: children, error: childError } = await supabase
      .from('children')
      .select('*')
      .in('id', childIds);

    if (childError) {
      setLoading(false);
      return;
    }

    const results = checkins.map((checkin) => {
      const child = children.find((c) => c.id === checkin.child_id);
      return { ...child, checkin_id: checkin.id, checked_in_at: checkin.checked_in_at, status: checkin.status, parent_waiting_at: checkin.parent_waiting_at };
    });

    setWaitingKids(results.filter((k) => k.status === 'parent_waiting'));
    setCheckedInKids(results.filter((k) => k.status === 'checked_in'));
    setLoading(false);
  };

  useEffect(() => {
    fetchKids();
    const interval = setInterval(fetchKids, 10000);
    return () => clearInterval(interval);
  }, []);

  const handleRelease = async (childId, checkinId, childName) => {
    const { error } = await supabase
      .from('checkins')
      .update({ status: 'checked_out', checked_out_at: new Date() })
      .eq('id', checkinId);

    if (error) {
      alert('Error releasing child: ' + error.message);
      return;
    }

    setWaitingKids(waitingKids.filter((k) => k.checkin_id !== checkinId));
    setSelectedKid(null);
  };

  const handleCheckOut = async (childId, checkinId, childName) => {
    const { error } = await supabase
      .from('checkins')
      .update({ status: 'checked_out', checked_out_at: new Date() })
      .eq('id', checkinId);

    if (error) {
      alert('Error checking out: ' + error.message);
      return;
    }

    setCheckedInKids(checkedInKids.filter((k) => k.checkin_id !== checkinId));
    setSelectedKid(null);
  };

  return (
    <div>
      {/* Kid Detail Modal */}
      {selectedKid && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <h2 style={{ marginBottom: '4px' }}>👶 {selectedKid.full_name}</h2>
            <p style={{ color: '#888', marginBottom: '20px' }}>
              {t.checkedInAtTime} {new Date(selectedKid.checked_in_at).toLocaleTimeString()}
            </p>

            <div style={detailSectionStyle}>
              <p className="section-title" style={{ marginBottom: '8px' }}>{t.parentInfo2}</p>
              <p><strong>{t.name}:</strong> {selectedKid.parent_name}</p>
              <p><strong>{t.phone}:</strong> {selectedKid.parent_phone}</p>
            </div>

            <div style={detailSectionStyle}>
              <p className="section-title" style={{ marginBottom: '8px' }}>{t.emergencyContactTitle}</p>
              {selectedKid.emergency_contact_name ? (
                <>
                  <p><strong>{t.name}:</strong> {selectedKid.emergency_contact_name}</p>
                  <p><strong>{t.phone}:</strong> {selectedKid.emergency_contact_phone || t.notProvided}</p>
                </>
              ) : (
                <p style={{ color: '#999' }}>{t.noEmergencyContact}</p>
              )}
            </div>

            {selectedKid.notes && (
              <div style={detailSectionStyle}>
                <p className="section-title" style={{ marginBottom: '8px' }}>{t.notes}</p>
                <p>{selectedKid.notes}</p>
              </div>
            )}

            {selectedKid.status === 'parent_waiting' && (
              <button
                className="btn btn-green"
                style={{ width: '100%', marginTop: '16px' }}
                onClick={() => handleRelease(selectedKid.id, selectedKid.checkin_id, selectedKid.full_name)}
              >
                {t.releaseToParent}
              </button>
            )}

            {selectedKid.status === 'checked_in' && (
              <button
                className="btn btn-red"
                style={{ width: '100%', marginTop: '16px' }}
                onClick={() => handleCheckOut(selectedKid.id, selectedKid.checkin_id, selectedKid.full_name)}
              >
                {t.checkOutChild(selectedKid.full_name)}
              </button>
            )}

            <button
              className="btn btn-gray"
              style={{ width: '100%', marginTop: '10px' }}
              onClick={() => setSelectedKid(null)}
            >
              {t.close}
            </button>
          </div>
        </div>
      )}

      <div className="header">
        <h2>{t.staffDashboardTitle}</h2>
        <p>{t.liveFeed}</p>
      </div>

      {waitingKids.length > 0 && (
        <div style={{ marginBottom: '24px' }}>
          <div style={{ ...statBoxStyle, backgroundColor: '#ff9800' }}>
            <h2>{waitingKids.length}</h2>
            <p>{t.parentWaiting(waitingKids.length)}</p>
          </div>
          <p className="section-title">{t.tapToRelease}</p>
          {waitingKids.map((kid) => (
            <div
              key={kid.checkin_id}
              className="kid-card"
              style={{ cursor: 'pointer', borderColor: '#ff9800', borderWidth: '2px' }}
              onClick={() => setSelectedKid(kid)}
            >
              <div className="kid-info">
                <h3>👶 {kid.full_name}</h3>
                <p>{t.parent}: {kid.parent_name}</p>
                <p style={{ color: '#ff9800' }}>⏳ {t.waitingSince} {new Date(kid.parent_waiting_at).toLocaleTimeString()}</p>
              </div>
              <span style={{ color: '#ff9800', fontSize: '20px' }}>›</span>
            </div>
          ))}
        </div>
      )}

      <div className="stat-box">
        <h2>{checkedInKids.length}</h2>
        <p>{t.childrenCheckedIn}</p>
      </div>

      {loading && <p style={{ textAlign: 'center', color: '#888' }}>{t.loading}</p>}

      {!loading && checkedInKids.length === 0 && waitingKids.length === 0 && (
        <p style={{ textAlign: 'center', color: '#999', marginTop: '20px' }}>
          {t.noChildrenIn}
        </p>
      )}

      {!loading && checkedInKids.length > 0 && (
        <div>
          <p className="section-title">{t.tapChildDetails}</p>
          {checkedInKids.map((kid) => (
            <div
              key={kid.checkin_id}
              className="kid-card"
              style={{ cursor: 'pointer' }}
              onClick={() => setSelectedKid(kid)}
            >
              <div className="kid-info">
                <h3>👶 {kid.full_name}</h3>
                <p>{t.parent}: {kid.parent_name}</p>
                <p>{t.inAt}: {new Date(kid.checked_in_at).toLocaleTimeString()}</p>
              </div>
              <span style={{ color: '#667eea', fontSize: '20px' }}>›</span>
            </div>
          ))}
        </div>
      )}

      <button className="btn btn-blue btn-small" style={{ marginTop: '20px', width: '100%' }} onClick={fetchKids}>
        {t.refresh}
      </button>
    </div>
  );
}

const overlayStyle = {
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  backgroundColor: 'rgba(0,0,0,0.5)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '20px',
};

const modalStyle = {
  background: 'white',
  borderRadius: '20px',
  padding: '30px',
  width: '100%',
  maxWidth: '400px',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};

const detailSectionStyle = {
  background: '#f8f9ff',
  borderRadius: '12px',
  padding: '16px',
  marginBottom: '12px',
};

const statBoxStyle = {
  background: 'linear-gradient(135deg, #667eea, #764ba2)',
  color: 'white',
  borderRadius: '16px',
  padding: '20px',
  textAlign: 'center',
  marginBottom: '24px',
};

export default Dashboard;